import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMyStorageRequests,
  getStorageRequestQuotes,
  createStorageCheckout,
  createStorageSubscription,
  getStoragePaymentStatus,
  createShipmentQuoteCheckout,
  cancelStorageGuardado,
  requestStorageWithdrawal,
  selfWithdrawStorageGuardado,
  renewStorageGuardado,
  cancelStorageSubscription,
  submitStorageReview,
  getStorageDepotReviews,
  type StorageReviewPayload,
  type StorageWithdrawalDestination
} from './api';
import {
  StorageRequestListSchema,
  StorageRequestQuotesSchema,
  StoragePaymentStatusSchema,
  StorageGuardadoActionResultSchema,
  StorageSubscriptionCancelResultSchema,
  StorageRenewalResultSchema,
  StorageSubmitReviewResultSchema,
  StorageWithdrawalResultSchema,
  StorageDepotReviewsSchema,
  type StorageOneTimePaymentStatus,
  type StoragePaymentStatus,
  type StorageRequest,
  type StorageSubscriptionPaymentStatus
} from 'contracts/storage';
import { hasStorageRequestAcceptedQuote, isStorageRenewalOrder, isStorageSubscriptionPending } from 'domain/storage/storage.rules';
import { translateKnownError } from 'utils/error-translator';
// Both importable under AGENTS.md §2 (`shared/`/`contracts/` are open to any feature) — the
// shipment-quotes-by-id fetch already lives in shared/api/quote.ts (split out of
// features/quotes/api.ts during NEX-1385 for exactly this kind of second consumer), so
// StorageParallelPaymentPage.tsx's shipment column reads it from there directly instead of
// duplicating the axios call the way createShipmentQuoteCheckout below has to (that one has no
// shared/ home — see its own comment in ./api.ts).
import { getQuotesByShipment } from 'shared/api/quote';
import { QuoteGroupListSchema, type QuoteGroup } from 'contracts/quote';

const POLL_INTERVAL = 5000;
// Mirrors shared/queries/payment.ts's own TERMINAL_STATUSES: nothing moves an order past one of
// these four (storage-payment.constants.ts's STORAGE_PAYMENT_ORDER_STATUS).
const TERMINAL_STATUSES = ['paid', 'expired', 'rejected', 'cancelled'];

/**
 * The `{ success, data }` envelope every storage endpoint answers inside (contracts/storage.ts's
 * header has the per-endpoint detail) — a typed cast past `AxiosResponse<unknown>`'s
 * `data: unknown`, not a validation step. The actual shape is checked by the Zod `.parse()` call
 * each site below makes right after unwrapping this; kept as a named interface rather than
 * `(res: any)` (which quotes/queries.ts's useVerificationStatus/useDriverReviews use for a
 * genuinely uncertain legacy shape) because this feature's envelope is fixed and confirmed.
 */
export interface StorageEnvelope<T> {
  data: T;
}

export const storageKeys = {
  all: ['storage'] as const,
  list: () => [...storageKeys.all, 'list'] as const,
  quotes: (requestId: number | string) => [...storageKeys.all, 'quotes', String(requestId)] as const,
  paymentStatus: (requestId: number | string) => [...storageKeys.all, 'payment-status', String(requestId)] as const,
  // Added for StorageParallelPaymentPage.tsx's shipment column — namespaced under this
  // feature's own key factory (not quotesKeys from features/quotes/queries.ts, which this
  // feature cannot import) even though it caches the same `GET /quotes/by-shipment/:id` data
  // features/quotes's own useQuotesByShipment keys under `quotesKeys.byShipment`. Two
  // independent cache entries for the same endpoint, not one shared — an acceptable
  // consequence of the coupling rule, not a bug.
  shipmentQuotes: (shipmentId: number | string) => [...storageKeys.all, 'shipment-quotes', String(shipmentId)] as const,
  // NEX-1329 (audit W5) — a depósito's reviews (contract C2).
  depotReviews: (storageProfileId: number | string) => [...storageKeys.all, 'depot-reviews', String(storageProfileId)] as const
};

const fetchMyStorageRequests = () =>
  getMyStorageRequests().then((res) => StorageRequestListSchema.parse((res.data as StorageEnvelope<{ requests: unknown }>).data.requests));

// NEX-1645 (plan 2026-10-20, Tarea 3.3) — `enabled: false` keeps the call from firing at all (the nav badge
// while the storage module is `off`).
export const useMyStorageRequests = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: storageKeys.list(),
    queryFn: fetchMyStorageRequests,
    enabled
  });

function findRequest(requests: StorageRequest[] | undefined, requestId: number | string): StorageRequest | null | undefined {
  if (!requests) return undefined;
  return requests.find((r) => String(r.id) === String(requestId)) ?? null;
}

/** NEX-1167 — nothing moves a request out of these by itself, so there is nothing left to wait for. */
function isRequestSettled(request: StorageRequest | null | undefined): boolean {
  if (request === undefined) return false;
  return request === null || hasStorageRequestAcceptedQuote(request) || request.status === 'cancelled' || request.status === 'expired';
}

/**
 * NEX-1167 — one of the client's requests out of `GET /storage/requests/mine`, polled until its
 * payment accepted a quote (`status: 'accepted'` + `acceptedQuoteId`, written together by the
 * webhook) or it can no longer be (cancelled, expired, not the client's). The payment return page
 * reads it once the payment is confirmed (either modality), for the depósito's address — NEX-1169
 * (plan 1.4): the subscription's own state comes from `useStoragePaymentStatus` now, not from this.
 * Same cache entry as `useMyStorageRequests` (the list page sees the acceptance too). `request`:
 * `undefined` until loaded, `null` when the request is not among the client's own. `poll: false`
 * stops polling (the caller's time limit).
 */
export function useStorageRequestAcceptance(
  requestId: number | string | null | undefined,
  { enabled = true, poll = true }: { enabled?: boolean; poll?: boolean } = {}
) {
  const query = useQuery({
    queryKey: storageKeys.list(),
    queryFn: fetchMyStorageRequests,
    enabled: enabled && !!requestId,
    retry: false,
    refetchInterval: (q) => (!poll || isRequestSettled(findRequest(q.state.data, requestId ?? '')) ? false : POLL_INTERVAL)
  });

  return {
    request: query.isSuccess ? findRequest(query.data, requestId ?? '') : undefined,
    loading: query.isLoading,
    error: query.isError ? extractErrorMessage(query.error) : null,
    refetch: query.refetch
  };
}

export const useStorageRequestQuotes = (requestId: number | string | undefined) =>
  useQuery({
    queryKey: storageKeys.quotes(requestId ?? ''),
    queryFn: () =>
      getStorageRequestQuotes(requestId as number | string).then((res) =>
        StorageRequestQuotesSchema.parse((res.data as StorageEnvelope<unknown>).data)
      ),
    enabled: !!requestId
  });

/**
 * `GET /quotes/by-shipment/:id` for StorageParallelPaymentPage.tsx's shipment column — a
 * hand-kept duplicate of features/quotes/queries.ts's own `useQuotesByShipment` (identical
 * queryFn/Zod parse), not an import of it (coupling rule — see this file's top-of-file
 * comment). Reads `shared/api/quote.ts` + `contracts/quote.ts` directly, both legitimately
 * importable here, so unlike `createShipmentQuoteCheckout` in ./api.ts, no axios call needed
 * duplicating — only the query wrapper itself.
 */
export const useShipmentQuotesForParallelPayment = (shipmentId: number | string | undefined) =>
  useQuery({
    queryKey: storageKeys.shipmentQuotes(shipmentId ?? ''),
    queryFn: () => getQuotesByShipment(shipmentId as number | string).then((res) => QuoteGroupListSchema.parse(res.data)),
    enabled: !!shipmentId
  });

// Re-exported purely so consumers (StorageParallelPaymentPage.tsx) can type the flattened
// per-quote rows they build from this without reaching into contracts/quote.ts themselves for
// a type ./queries.ts already imports.
export type { QuoteGroup };

// A storage request is published only from the shipment wizard (features/shipments' own
// `useCreateStorageRequestFromWizard`, AGENTS.md §2), so this feature has no mutation for
// `POST /storage/requests` — the unused duplicate was removed in the integración final (knip).

// No onSuccess invalidation: same reasoning features/quotes/queries.ts's useCreateCheckout
// documents for the shipment flow — a successful call only means the checkout/subscription was
// *initiated*, not completed. The caller (a later UI phase) invalidates storageKeys.list()/
// .quotes()/.paymentStatus() explicitly once it knows the flow actually resolved.
export const useCreateStorageCheckout = () =>
  useMutation({
    mutationFn: ({ storageQuoteId, options }: { storageQuoteId: number | string; options?: Record<string, unknown> }) =>
      createStorageCheckout(storageQuoteId, options)
  });

// NEX-1169 (plan 2026-10-20 task 1.5) — `autoRenew`: the contracting modal's «Renovar automáticamente cada mes».
export const useCreateStorageSubscription = () =>
  useMutation({
    mutationFn: ({ storageQuoteId, autoRenew = false }: { storageQuoteId: number | string; autoRenew?: boolean }) =>
      createStorageSubscription(storageQuoteId, { autoRenew })
  });

// No onSuccess invalidation, same reasoning as the two mutations above — a successful call
// only means the shipment-side checkout was *initiated*.
export const useCreateShipmentQuoteCheckout = () =>
  useMutation({
    mutationFn: ({ quoteId, options }: { quoteId: number | string; options?: Record<string, unknown> }) =>
      createShipmentQuoteCheckout(quoteId, options)
  });

/**
 * Typed casts past `AxiosResponse<unknown>`'s `data: unknown` for the three payment-init
 * mutations above — same spirit as `StorageEnvelope<T>`, not a Zod validation step. Deliberately
 * NOT promoted to a `contracts/storage.ts` schema: StorageParallelPaymentPage.tsx only ever
 * reads one URL field off each of these responses (`checkoutUrl`/`initPoint`, to
 * `window.open`), and this task's backend ground truth gives the full field list for all
 * three — but a `.parse()` that throws the whole combined-payment action over an unrelated,
 * unread field (e.g. `sandboxCheckoutUrl`'s exact nullability, never confirmed) is more risk
 * than that one read site needs. Mirrors features/quotes/queries.ts's own choice not to
 * Zod-parse `createCheckout`'s result either (QuotesPage.tsx unwraps it by hand, cast to
 * `any`). Promote to contracts/storage.ts if a second consumer needs the rest of the shape.
 */
export interface ShipmentCheckoutResult {
  checkoutUrl?: string | null;
  simulated?: boolean;
}

export interface StoragePaymentInitResult {
  checkoutUrl?: string | null;
  initPoint?: string | null;
  // NEX-1170 — the checkout's order (create-storage-checkout.handler.ts); a subscription has none.
  paymentOrderId?: number | string | null;
}

function isTerminalStatus(status: string | null | undefined): boolean {
  return !!status && TERMINAL_STATUSES.includes(status);
}

// NEX-1169 (plan 1.4) — the two answers of GET /storage/payment/status (contracts/storage.ts).
function isSubscriptionAnswer(data: StoragePaymentStatus | null | undefined): data is StorageSubscriptionPaymentStatus {
  return data?.modality === 'subscription';
}

function oneTimeOrderOf(data: StoragePaymentStatus | null | undefined): StorageOneTimePaymentStatus | null {
  return data && !isSubscriptionAnswer(data) ? data : null;
}

/** Nothing left to wait for: an order in a final status, or a subscription that is not `pending`. */
function isPaymentAnswerSettled(data: StoragePaymentStatus | null | undefined): boolean {
  if (isSubscriptionAnswer(data)) return !isStorageSubscriptionPending(data.status);
  return isTerminalStatus(oneTimeOrderOf(data)?.status);
}

// NEX-1151 (audit W1) — in Spanish: a known translation of the API's error, never its raw (English)
// message, else a generic text. Shared by the payment-status poll and the acceptance poll.
function extractErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string; code?: string } } } | null;
  return (
    translateKnownError(e?.response?.data?.message, e?.response?.data?.code) ??
    'No pudimos consultar el estado de tu depósito. Intentá nuevamente en unos minutos.'
  );
}

/**
 * Payment-status polling for the storage flow — copied almost verbatim from
 * shared/queries/payment.ts's usePaymentStatus (same terminal-status list, same POLL_INTERVAL,
 * same derived-object return shape), with two differences: it reads features/storage/api.ts's
 * getStoragePaymentStatus instead of the shipment one, and it Zod-parses the response via
 * contracts/storage.ts's StoragePaymentStatusSchema (that schema exists for exactly this
 * reason — the shipment-side hook predates this repo's "Zod .parse() at the boundary" rule and
 * was out of this task's scope to retrofit). Also surfaces storageQuoteId/storageRequestId,
 * which the payment-status payload carries and the shipment equivalent does not.
 *
 * NEX-1169 (plan 1.4) — the endpoint answers the monthly subscription too. The order fields
 * (`status`, `paymentOrderId`, `checkoutUrl`, `hasActivePayment`…) keep meaning the pago único's
 * order only — `null`/`false` when the answer is a subscription, so no caller reads a subscription
 * as an order in flight or as a failed payment — and the subscription comes apart in `subscription`
 * (`null` otherwise); `modality` says which one answered. A subscription is polled only while
 * `pending` (waiting for Mercado Pago).
 *
 * NEX-1169 (plan 2026-10-20 task 1.8) — the order may be a renewal of a one-time guardado (the API answers the
 * most recent order, so an abandoned renewal shows instead of the contract's paid one): `renewal` is its months
 * and period (`null` for the contract's own order). The order fields keep describing that order; a caller that
 * reads them as "the depósito's payment" checks `renewal` first.
 */
// NEX-1167 — `poll: false` stops polling before a terminal status (the payment return page's time limit).
export function useStoragePaymentStatus(
  requestId: number | string | null | undefined,
  { enabled = true, poll = true }: { enabled?: boolean; poll?: boolean } = {}
) {
  const query = useQuery({
    queryKey: storageKeys.paymentStatus(requestId ?? ''),
    queryFn: () =>
      getStoragePaymentStatus(requestId as number | string).then((res) =>
        StoragePaymentStatusSchema.nullable().parse((res.data as StorageEnvelope<unknown>).data)
      ),
    enabled: enabled && !!requestId,
    retry: false,
    refetchInterval: (query) => (!poll || isPaymentAnswerSettled(query.state.data) ? false : POLL_INTERVAL)
  });

  const data = query.data;
  const order = oneTimeOrderOf(data);
  const subscription = isSubscriptionAnswer(data) ? data : null;
  const isTerminal = isTerminalStatus(order?.status);
  // NEX-1169 (plan 2026-10-20 task 1.8) — the order is a renewal, not the contract's payment.
  const renewal =
    order && isStorageRenewalOrder(order)
      ? { months: order.renewalMonths as number, periodStart: order.periodStart ?? null, periodEnd: order.periodEnd ?? null }
      : null;

  return {
    modality: subscription ? ('subscription' as const) : order ? ('one_time' as const) : null,
    status: order?.status || null,
    remainingSeconds: order?.remainingSeconds || null,
    checkoutUrl: order?.checkoutUrl || null,
    paymentOrderId: order?.paymentOrderId || null,
    amount: order?.amount || null,
    displayAmount: order?.displayAmount || null,
    currency: data?.currency || 'UYU',
    expiresAt: order?.expiresAt || null,
    storageQuoteId: order?.storageQuoteId ?? null,
    storageRequestId: order?.storageRequestId ?? null,
    renewal,
    subscription,
    isTerminal,
    hasActivePayment: !!order && !isTerminal,
    loading: query.isLoading,
    // NEX-1167 — true once the endpoint answered, so a `status: null` reads as "no order", not "not yet".
    loaded: query.isSuccess,
    error: query.isError ? extractErrorMessage(query.error) : null,
    refetch: query.refetch
  };
}

// ── NEX-1170 — the client's guardado lifecycle (plan Fase 8.2-8.5) ──

/**
 * After any lifecycle action the request changed (status, end date, withdrawal draft): both reads that
 * carry it — the list (`GET /storage/requests/mine`, with `paymentModality`) and the request with its
 * quotes (`GET /storage/requests/:id/quotes`, what StorageManagePage renders) — are fetched again.
 * NEX-1169 (plan 2026-10-20 task 1.5) — and its payment status: a monthly baja, «Retiré por mi cuenta» and
 * «Cancelar el cobro mensual» cancel the subscription in Mercado Pago.
 */
function useInvalidateGuardado() {
  const queryClient = useQueryClient();
  return (requestId: number | string) => {
    queryClient.invalidateQueries({ queryKey: storageKeys.list() });
    queryClient.invalidateQueries({ queryKey: storageKeys.quotes(requestId) });
    queryClient.invalidateQueries({ queryKey: storageKeys.paymentStatus(requestId) });
  };
}

const parseGuardadoAction = (res: { data: unknown }) =>
  StorageGuardadoActionResultSchema.parse((res.data as StorageEnvelope<unknown>).data);

/** "Dar de baja" — answers the end date the API calculated and whether Nexvia cancels the subscription by hand. */
export const useCancelStorageGuardado = () => {
  const invalidate = useInvalidateGuardado();
  return useMutation({
    mutationFn: (requestId: number | string) => cancelStorageGuardado(requestId).then(parseGuardadoAction),
    onSuccess: (_data, requestId) => invalidate(requestId)
  });
};

/**
 * NEX-1169 (plan 2026-10-20 task 1.5) — «Cancelar el cobro mensual»: the subscription cancelled in Mercado Pago
 * and the guardado `ending` on the last day charged; `subscriptionCancellation: 'manual_pending'` when Mercado
 * Pago failed (Nexvia cancels it by hand).
 */
export const useCancelStorageSubscription = () => {
  const invalidate = useInvalidateGuardado();
  return useMutation({
    mutationFn: (requestId: number | string) =>
      cancelStorageSubscription(requestId).then((res) =>
        StorageSubscriptionCancelResultSchema.parse((res.data as StorageEnvelope<unknown>).data)
      ),
    onSuccess: (_data, requestId) => invalidate(requestId)
  });
};

/**
 * NEX-1169 (plan 2026-10-20 task 1.8) — «Renovar» a one-time guardado: a new pago único checkout for `months`
 * more months, with the period and amount the API calculated. Nothing about the guardado changes until Mercado
 * Pago approves it (the webhook moves `endsAt`), so only the payment status is asked again (the renewal is its
 * newest order) — also on 409 PAYMENT_ALREADY_IN_PROGRESS (another renewal waits for its payment).
 */
export const useRenewStorageGuardado = () => {
  const queryClient = useQueryClient();
  const refreshPayment = (requestId: number | string) => queryClient.invalidateQueries({ queryKey: storageKeys.paymentStatus(requestId) });
  return useMutation({
    mutationFn: ({ requestId, months }: { requestId: number | string; months: number }) =>
      renewStorageGuardado(requestId, months).then((res) => StorageRenewalResultSchema.parse((res.data as StorageEnvelope<unknown>).data)),
    onSuccess: (_data, { requestId }) => refreshPayment(requestId),
    onError: (error, { requestId }) => {
      const code = (error as { response?: { data?: { code?: string } } } | null)?.response?.data?.code;
      if (code === 'PAYMENT_ALREADY_IN_PROGRESS') refreshPayment(requestId);
    }
  });
};

/** "Solicitar retiro" — answers the shipment draft the wizard opens. */
export const useRequestStorageWithdrawal = () => {
  const invalidate = useInvalidateGuardado();
  return useMutation({
    mutationFn: ({ requestId, destination }: { requestId: number | string; destination: StorageWithdrawalDestination }) =>
      requestStorageWithdrawal(requestId, destination).then((res) =>
        StorageWithdrawalResultSchema.parse((res.data as StorageEnvelope<unknown>).data)
      ),
    onSuccess: (_data, { requestId }) => invalidate(requestId)
  });
};

/** "Retiré por mi cuenta" — closes the guardado (a monthly one's charge is cancelled in Mercado Pago; by hand only if that failed). */
export const useSelfWithdrawStorageGuardado = () => {
  const invalidate = useInvalidateGuardado();
  return useMutation({
    mutationFn: (requestId: number | string) => selfWithdrawStorageGuardado(requestId).then(parseGuardadoAction),
    onSuccess: (_data, requestId) => invalidate(requestId)
  });
};

// ── NEX-1329 — the client rates the depósito (plan Fase 10.3, decision A6) ──

/**
 * "Calificá al depósito" — once answered, the list (`GET /storage/requests/mine`, where `canReview`,
 * `myReview` and `theirReview` live) and the quotes (the depósito's average) are fetched again. Also
 * on 409 REVIEW_ALREADY_SUBMITTED (rated from another tab): the card is stale, and still shows (with
 * the client's review) under the dialog's message.
 */
export const useSubmitStorageReview = () => {
  const invalidate = useInvalidateGuardado();
  return useMutation({
    mutationFn: ({ requestId, payload }: { requestId: number | string; payload: StorageReviewPayload }) =>
      submitStorageReview(requestId, payload).then((res) =>
        StorageSubmitReviewResultSchema.parse((res.data as StorageEnvelope<unknown>).data)
      ),
    onSuccess: (_data, { requestId }) => invalidate(requestId),
    onError: (error, { requestId }) => {
      const code = (error as { response?: { data?: { code?: string } } } | null)?.response?.data?.code;
      if (code === 'REVIEW_ALREADY_SUBMITTED') invalidate(requestId);
    }
  });
};

/**
 * NEX-1329 (audit W5) — the depósito's reviews from its clients (`GET /storage/depots/:storageProfileId/
 * reviews`, contract C2): average, count and the latest reviews, without any data of who wrote them.
 * Fetched only while the dialog is open, like features/quotes' useDriverReviews.
 */
export const useStorageDepotReviews = (storageProfileId: number | string | null | undefined, enabled: boolean) =>
  useQuery({
    queryKey: storageKeys.depotReviews(storageProfileId ?? ''),
    queryFn: () =>
      getStorageDepotReviews(storageProfileId as number | string).then((res) =>
        StorageDepotReviewsSchema.parse((res.data as StorageEnvelope<unknown>).data)
      ),
    enabled: enabled && storageProfileId != null && storageProfileId !== ''
  });

export type { StorageWithdrawalDestination };
